import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ─── Auth Store ───────────────────────────────────────────────────────────────

interface AuthState {
  user: {
    id: string;
    role: string;
    phone: string;
  } | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setAuth: (data: { userId: string; role: string; accessToken: string; refreshToken: string; phone?: string }) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setAuth: ({ userId, role, accessToken, refreshToken, phone = '' }) => {
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        set({
          user: { id: userId, role, phone },
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },
    }),
    { name: 'auth-storage' }
  )
);

// ─── Cart Store ───────────────────────────────────────────────────────────────

export type CartType = 'food' | 'market';

export interface CartItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  notes?: string;
}

interface CartState {
  foodCart: CartItem[];
  marketCart: CartItem[];
  addItem: (type: CartType, item: CartItem) => void;
  removeItem: (type: CartType, productId: string) => void;
  updateQuantity: (type: CartType, productId: string, quantity: number) => void;
  updateNotes: (type: CartType, productId: string, notes: string) => void;
  clearCart: (type: CartType) => void;
  getTotal: (type: CartType) => number;
  getItemCount: (type: CartType) => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      foodCart: [],
      marketCart: [],

      addItem: (type, item) => {
        const cartKey = type === 'food' ? 'foodCart' : 'marketCart';
        const cart = get()[cartKey];
        const existing = cart.find((i) => i.productId === item.productId);

        if (existing) {
          set({
            [cartKey]: cart.map((i) =>
              i.productId === item.productId ? { ...i, quantity: i.quantity + item.quantity } : i
            ),
          });
        } else {
          set({ [cartKey]: [...cart, item] });
        }
      },

      removeItem: (type, productId) => {
        const cartKey = type === 'food' ? 'foodCart' : 'marketCart';
        set({ [cartKey]: get()[cartKey].filter((i) => i.productId !== productId) });
      },

      updateQuantity: (type, productId, quantity) => {
        const cartKey = type === 'food' ? 'foodCart' : 'marketCart';
        if (quantity <= 0) {
          set({ [cartKey]: get()[cartKey].filter((i) => i.productId !== productId) });
        } else {
          set({
            [cartKey]: get()[cartKey].map((i) =>
              i.productId === productId ? { ...i, quantity } : i
            ),
          });
        }
      },

      updateNotes: (type, productId, notes) => {
        const cartKey = type === 'food' ? 'foodCart' : 'marketCart';
        set({
          [cartKey]: get()[cartKey].map((i) =>
            i.productId === productId ? { ...i, notes } : i
          ),
        });
      },

      clearCart: (type) => {
        set({ [type === 'food' ? 'foodCart' : 'marketCart']: [] });
      },

      getTotal: (type) => {
        const cart = type === 'food' ? get().foodCart : get().marketCart;
        return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      },

      getItemCount: (type) => {
        const cart = type === 'food' ? get().foodCart : get().marketCart;
        return cart.reduce((sum, item) => sum + item.quantity, 0);
      },
    }),
    { name: 'cart-storage' }
  )
);

// ─── UI Store ─────────────────────────────────────────────────────────────────

interface UIState {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarOpen: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
}));
